import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCircle2, Factory, ShoppingCart, Truck, Package, CreditCard, Boxes, FileText, AlertTriangle, ClipboardList } from 'lucide-react';
import { dismissAllSystemNotifications, syncBadgeFromServer } from '../lib/notifyBridge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MODULE_ICON = {
  sales:        ShoppingCart,
  production:   Factory,
  dispatch:     Truck,
  inventory:    Boxes,
  purchases:    Package,
  payments:     CreditCard,
  debtors:      CreditCard,
  creditors:    CreditCard,
  vouchers:     FileText,
  enterprise:   FileText,
  invoices:     FileText,
  samples:      ClipboardList,
  compliance:   AlertTriangle,
  general:      Bell,
};

const MODULE_COLOR = {
  sales:        'text-blue-400 bg-blue-500/10',
  production:   'text-amber-400 bg-amber-500/10',
  dispatch:     'text-green-400 bg-green-500/10',
  inventory:    'text-cyan-400 bg-cyan-500/10',
  purchases:    'text-orange-400 bg-orange-500/10',
  payments:     'text-emerald-400 bg-emerald-500/10',
  debtors:      'text-emerald-400 bg-emerald-500/10',
  creditors:    'text-rose-400 bg-rose-500/10',
  vouchers:     'text-purple-400 bg-purple-500/10',
  enterprise:   'text-amber-400 bg-amber-500/10',
  invoices:     'text-amber-400 bg-amber-500/10',
  samples:      'text-pink-400 bg-pink-500/10',
  compliance:   'text-red-400 bg-red-500/10',
  general:      'text-zinc-400 bg-zinc-500/10',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-IN');
};

export function NotificationDropdown({ open, onClose, onCountChange }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifs = async () => {
    try {
      const res = await axios.get(`${API}/notifications/`, { withCredentials: true });
      setNotifs(res.data || []);
      setError(null);
    } catch (e) {
      setError('Could not load');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { setLoading(true); fetchNotifs(); } }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  const markRead = async (id) => {
    if (String(id).startsWith('sample-reminder-')) return;
    try { await axios.patch(`${API}/notifications/${id}/read`, {}, { withCredentials: true }); } catch {}
  };

  const handleClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    onClose();
    if (n.link) navigate(n.link);
    if (onCountChange) onCountChange();
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifs(prev => prev.map(n => ({ ...n, is_read: true })));
      if (onCountChange) onCountChange();
      // Mirror to OS — clear every Tirth ERP banner from the system tray
      // and zero-out the app-icon badge in one go.
      dismissAllSystemNotifications();
      syncBadgeFromServer();
    } catch {}
  };

  if (!open) return null;

  const unreadCount = notifs.filter(n => !n.is_read).length;

  return (
    <div
      ref={panelRef}
      data-testid="notification-dropdown"
      className="absolute right-0 top-full mt-2 w-[380px] max-w-[95vw] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl z-50 overflow-hidden"
      style={{ maxHeight: 'min(560px, 80vh)' }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between bg-zinc-950">
        <div>
          <div className="text-zinc-50 text-sm font-semibold flex items-center gap-2">
            <Bell size={14} className="text-amber-400" />
            Notifications
            {unreadCount > 0 && (
              <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>
            )}
          </div>
          <div className="text-zinc-500 text-[10px] mt-0.5">For your modules · you won't see items you created</div>
        </div>
        <div className="flex items-center gap-1.5">
          {unreadCount > 0 && (
            <button data-testid="mark-all-read-btn" onClick={markAllRead}
              className="text-[11px] text-amber-400 hover:text-amber-300 px-2 py-1 rounded hover:bg-zinc-800">
              Mark all read
            </button>
          )}
          <button onClick={onClose} className="p-1 rounded hover:bg-zinc-800 text-zinc-500">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(min(560px, 80vh) - 96px)' }}>
        {loading && (
          <div className="p-6 text-center text-zinc-500 text-xs animate-pulse">Loading…</div>
        )}
        {!loading && error && (
          <div className="p-6 text-center text-red-400 text-xs">{error}</div>
        )}
        {!loading && !error && notifs.length === 0 && (
          <div className="p-8 text-center">
            <CheckCircle2 size={28} className="text-green-500 mx-auto mb-2 opacity-60" />
            <div className="text-zinc-400 text-sm font-medium">You're all caught up</div>
            <div className="text-zinc-600 text-xs mt-1">New alerts will land here</div>
          </div>
        )}
        {!loading && notifs.slice(0, 40).map(n => {
          const Icon = MODULE_ICON[n.module] || Bell;
          const colorCls = MODULE_COLOR[n.module] || MODULE_COLOR.general;
          return (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-zinc-800/60 last:border-0 hover:bg-zinc-800/40 transition-colors flex gap-3 ${!n.is_read ? 'bg-amber-500/[0.03]' : ''}`}
            >
              <div className={`w-8 h-8 rounded-lg ${colorCls} flex items-center justify-center flex-shrink-0`}>
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className={`text-xs font-semibold truncate ${!n.is_read ? 'text-zinc-50' : 'text-zinc-300'}`}>
                    {n.title}
                  </div>
                  {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0 mt-1.5" />}
                </div>
                <div className="text-zinc-400 text-[11px] mt-0.5 line-clamp-2">{n.message}</div>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-zinc-600 text-[10px]">{timeAgo(n.created_at)}</span>
                  {n.created_by && n.created_by !== 'System' && (
                    <>
                      <span className="text-zinc-700">·</span>
                      <span className="text-zinc-500 text-[10px]">by {n.created_by}</span>
                    </>
                  )}
                  {n.priority === 'high' && (
                    <span className="text-red-400 text-[9px] font-bold uppercase">High</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-zinc-800 bg-zinc-950 flex items-center justify-between gap-2">
        <button
          data-testid="notif-prefs-btn"
          onClick={() => { onClose(); navigate('/notifications/preferences'); }}
          className="text-[11px] text-zinc-400 hover:text-amber-400 py-1.5"
        >
          ⚙ Preferences
        </button>
        <button
          data-testid="view-all-notifs-btn"
          onClick={() => { onClose(); navigate('/notifications'); }}
          className="text-[11px] text-amber-400 hover:text-amber-300 py-1.5"
        >
          View all alerts →
        </button>
      </div>
    </div>
  );
}
